import { useMemo } from "react";
import { HomeBrandsRawNormDataItem } from "@api/useHomeBrandsRawNormData";
import { useDisplayMetricContext, useHomeContext } from "@pages/Home/HomeContext";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { getRem } from "@datassential/platform-ui-lib";
import { useGetAmongRawTableCell } from "@vms/TableCells/useGetAmongRawTableCell";
import { AmongPayload } from "@vms/SelectAmong/useGetAmongPayload";
import { getAmongTypeKey, useGetSelectedAmongTypes } from "@models/Among";
import { useGetEmptyTableCells } from "@vms/TableCells/useGetEmptyTableCells";
import { useGetAmongNormTableCell } from "@vms/TableCells/useGetAmongNormTableCell";
import { COMMON_NUM_COL_WIDTH_PX, HomeShow } from "@core/constants";

export type BrandsViewRawNormTableConfigProps = {
    data: HomeBrandsRawNormDataItem[];
    show: HomeShow;
    amongPayload: AmongPayload;
}

export function useBrandsViewRawNormTableConfig({
    data,
    show,
    amongPayload,
}: BrandsViewRawNormTableConfigProps) {
    const { search } = useHomeContext();
    const trimmedSearch = useMemo(() => search.trim(), [search]);
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const { displayMetricId } = useDisplayMetricContext();
    const isBaseSizeView = useMemo(
        () => displayMetricId === null,
        [displayMetricId]
    );

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const getEmptyCells = useGetEmptyTableCells();
    const getRawCell = useGetAmongRawTableCell(data);
    const getNormCell = useGetAmongNormTableCell(data);

    const getSelectedAmongTypes = useGetSelectedAmongTypes();
    const amongColumns = getSelectedAmongTypes(amongPayload);

    const colWidth = useMemo(
        () => getRem(COMMON_NUM_COL_WIDTH_PX * 11 / amongColumns.length),
        [amongColumns.length]
    );

    const paddingCols = useMemo(
        () => {
            if (amongColumns.length === 0) {
                return getEmptyCells(11);
            }

            return [];
        },
        [amongColumns, getEmptyCells]
    );

    return useMemo(
        () => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
                style: {
                    ...nameColumn.style,
                    cell: {
                        ...nameColumn.style?.cell,
                        flex: `1 0 ${getRem(190)}`,
                    }
                },
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            ...amongColumns.flatMap(i => {
                const raw = getRawCell(i.id, i.type, 'raw');
                const base = getRawCell(i.id, i.type, 'base');
                const norm = getNormCell(i.id, i.type, 'norm');

                return [
                    {
                        ...raw,
                        isHiddenForTextMode: true,
                        isHidden: show !== HomeShow.Raw || isBaseSizeView,
                        sortKey: hideSorting ? undefined : `column${getAmongTypeKey(i.id, i.type)}`,
                        style: {
                            ...raw.style,
                            cell: {
                                ...raw.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                    {
                        ...norm,
                        isHiddenForTextMode: true,
                        isHidden: show !== HomeShow.Norm || isBaseSizeView,
                        sortKey: hideSorting ? undefined : `column${getAmongTypeKey(i.id, i.type)}`,
                        style: {
                            ...norm.style,
                            cell: {
                                ...norm.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                    {
                        ...base,
                        isHiddenForTextMode: true,
                        isHidden: !isBaseSizeView,
                        sortKey: hideSorting ? undefined : `column${getAmongTypeKey(i.id, i.type)}`,
                        style: {
                            ...base.style,
                            cell: {
                                ...base.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                ];
            }),
            {
                key: 'brands-copy-raw',
                isHiddenForRender: true,
                isHidden: isBaseSizeView,
                head: '',
                headText: 'Raw',
                Value: () => null,
                subColumns: amongColumns.map(i => getRawCell(
                    i.id,
                    i.type,
                    'copy-raw'
                )),
            },
            {
                key: 'brands-copy-norm',
                isHiddenForRender: true,
                isHidden: isBaseSizeView,
                head: '',
                headText: 'Norms',
                Value: () => null,
                subColumns: amongColumns.map(i => getNormCell(
                    i.id,
                    i.type,
                    'copy-norm',
                )),
            },
            {
                key: 'brands-copy-base',
                isHiddenForRender: true,
                isHidden: !isBaseSizeView,
                head: '',
                headText: 'Base',
                Value: () => null,
                subColumns: amongColumns.map(i => getRawCell(
                    i.id,
                    i.type,
                    'copy-base',
                )),
            },
            ...paddingCols,
        ],
        [
            nameColumn,
            macStageColumn,
            amongColumns,
            getRawCell,
            hideSorting,
            getNormCell,
            show,
            colWidth,
            paddingCols,
            isBaseSizeView,
        ]
    );
}